'use client'

import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import { Box, Button, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'

import type { AssistantClarificationQuestion } from '~/features/assistant/streamTypes'

type Props = {
  intro?: string
  questions: AssistantClarificationQuestion[]
  disabled?: boolean
  /**
   * Selected answer labels by question id. When set, the card renders as an answered transcript item
   * (question + checked answer) instead of interactive inputs.
   */
  answeredLabels?: Record<string, string>
  /** When true, renders a non-interactive state indicating the clarification was skipped (new message sent without answering). */
  retired?: boolean
  /** Called with the verbose answers for the model, the friendly text for the chat bubble, and per-question labels. */
  onSubmit: (answerText: string, displayText: string, answers: Record<string, string>) => void
}

type TimeRangeAnswer = { from: string; to: string }
type AnswerValue = string | string[] | TimeRangeAnswer

const PRESETS: { id: string; labelKey: 'last24h' | 'last7d' | 'last30d'; hours: number }[] = [
  { id: '24h', labelKey: 'last24h', hours: 24 },
  { id: '7d', labelKey: 'last7d', hours: 24 * 7 },
  { id: '30d', labelKey: 'last30d', hours: 24 * 30 }
]

export default function ClarificationCard({ intro, questions, disabled, answeredLabels, retired, onSubmit }: Props) {
  const t = useTranslations('generativeDashboard.page')
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({})

  const setAnswer = useCallback((id: string, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [id]: value }))
  }, [])

  const allAnswered = useMemo(
    () => questions.every((q) => isAnswered(answers[q.id])),
    [questions, answers]
  )

  const submit = useCallback(() => {
    if (!allAnswered) return
    const labels = buildAnswerLabels(questions, answers)
    onSubmit(buildAnswerText(questions, answers), buildDisplayText(questions, labels), labels)
  }, [allAnswered, onSubmit, questions, answers])

  // Retired state: user sent a new message without answering, render as skipped/non-interactive.
  if (retired) {
    return (
      <Card variant="outlined" sx={{ opacity: 0.6 }}>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" color="text.secondary">
              {intro || t('clarifyIntro')}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              {t('skippedClarification')}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    )
  }

  // Answered transcript state: questions with their checked answers, no inputs.
  if (answeredLabels) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">{intro || t('clarifyIntro')}</Typography>
            {questions.map((question) => (
              <Box key={question.id}>
                <Typography variant="body2" color="text.secondary">
                  {question.prompt}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                  <CheckCircleOutline fontSize="small" color="success" />
                  <Typography variant="body2" fontWeight={500}>
                    {answeredLabels[question.id] ?? '—'}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle2">{intro || t('clarifyIntro')}</Typography>
          {questions.map((question) => (
            <Box key={question.id}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {question.prompt}
              </Typography>
              <QuestionInput
                question={question}
                value={answers[question.id]}
                disabled={disabled}
                onChange={(value) => setAnswer(question.id, value)}
              />
            </Box>
          ))}
          <Box>
            <Button variant="contained" size="small" disabled={disabled || !allAnswered} onClick={submit}>
              {t('submitAnswers')}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

function QuestionInput({
  question,
  value,
  disabled,
  onChange
}: {
  question: AssistantClarificationQuestion
  value: AnswerValue | undefined
  disabled?: boolean
  onChange: (value: AnswerValue) => void
}) {
  if (question.type === 'time_range') {
    return <TimeRangeInput value={isTimeRange(value) ? value : undefined} disabled={disabled} onChange={onChange} />
  }

  const options = question.options ?? []
  if (question.type === 'multi') {
    const selected = Array.isArray(value) ? value : []
    return (
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {options.map((option) => {
          const optionValue = option.value ?? option.id
          const isOn = selected.includes(optionValue)
          return (
            <Chip
              key={option.id}
              label={option.label}
              color={isOn ? 'primary' : 'default'}
              variant={isOn ? 'filled' : 'outlined'}
              disabled={disabled}
              onClick={() =>
                onChange(isOn ? selected.filter((v) => v !== optionValue) : [...selected, optionValue])
              }
            />
          )
        })}
      </Stack>
    )
  }

  // single / scope
  const selectedValue = typeof value === 'string' ? value : undefined
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {options.map((option) => {
        const optionValue = option.value ?? option.id
        const isOn = selectedValue === optionValue
        return (
          <Chip
            key={option.id}
            label={option.label}
            color={isOn ? 'primary' : 'default'}
            variant={isOn ? 'filled' : 'outlined'}
            disabled={disabled}
            onClick={() => onChange(optionValue)}
          />
        )
      })}
    </Stack>
  )
}

function TimeRangeInput({
  value,
  disabled,
  onChange
}: {
  value: TimeRangeAnswer | undefined
  disabled?: boolean
  onChange: (value: TimeRangeAnswer) => void
}) {
  const t = useTranslations('generativeDashboard.page')

  const applyPreset = (hours: number) => {
    const to = new Date()
    const from = new Date(to.getTime() - hours * 60 * 60 * 1000)
    onChange({ from: from.toISOString(), to: to.toISOString() })
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {PRESETS.map((preset) => (
          <Chip
            key={preset.id}
            label={t(preset.labelKey)}
            variant="outlined"
            disabled={disabled}
            onClick={() => applyPreset(preset.hours)}
          />
        ))}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          label={t('from')}
          type="datetime-local"
          size="small"
          disabled={disabled}
          value={toLocalInput(value?.from)}
          onChange={(event) => onChange({ from: fromLocalInput(event.target.value), to: value?.to ?? '' })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t('to')}
          type="datetime-local"
          size="small"
          disabled={disabled}
          value={toLocalInput(value?.to)}
          onChange={(event) => onChange({ from: value?.from ?? '', to: fromLocalInput(event.target.value) })}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
    </Stack>
  )
}

function isAnswered(value: AnswerValue | undefined): boolean {
  if (value === undefined) return false
  if (Array.isArray(value)) return value.length > 0
  if (isTimeRange(value)) return Boolean(value.from && value.to)
  return value.trim().length > 0
}

function isTimeRange(value: AnswerValue | undefined): value is TimeRangeAnswer {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && 'from' in value && 'to' in value
}

function buildAnswerText(
  questions: AssistantClarificationQuestion[],
  answers: Record<string, AnswerValue>
): string {
  const lines = questions.map((question) => {
    const value = answers[question.id]
    const label = optionLabel(question, value)
    return `- ${question.prompt} ${label}`
  })
  return `Here are my answers:\n${lines.join('\n')}`
}

/** Friendly per-question answer labels for the answered card (e.g. "Last 2 weeks", "Entire organization"). */
function buildAnswerLabels(
  questions: AssistantClarificationQuestion[],
  answers: Record<string, AnswerValue>
): Record<string, string> {
  const labels: Record<string, string> = {}
  for (const question of questions) {
    labels[question.id] = displayLabel(question, answers[question.id])
  }
  return labels
}

/** Friendly chat-bubble text shown in place of the verbose model message (e.g. "Entire organization · Jun 3 – Jun 10"). */
function buildDisplayText(questions: AssistantClarificationQuestion[], labels: Record<string, string>): string {
  return questions
    .map((question) => labels[question.id])
    .filter(Boolean)
    .join(' · ')
}

function displayLabel(question: AssistantClarificationQuestion, value: AnswerValue | undefined): string {
  if (value === undefined) return ''
  if (isTimeRange(value)) return formatRangeLabel(value)
  if (Array.isArray(value)) return value.map((v) => optionDisplay(question, v)).join(', ')
  return optionDisplay(question, value)
}

/** Like {@link labelForValue} but without the raw value in parentheses — for human display only. */
function optionDisplay(question: AssistantClarificationQuestion, value: string): string {
  const match = question.options?.find((option) => (option.value ?? option.id) === value)
  return match ? match.label : value
}

function formatRangeLabel({ from, to }: TimeRangeAnswer): string {
  const fromDate = new Date(from)
  const toDate = new Date(to)
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return `${from} – ${to}`
  }
  const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
  return `${formatter.format(fromDate)} – ${formatter.format(toDate)}`
}

function optionLabel(question: AssistantClarificationQuestion, value: AnswerValue | undefined): string {
  if (value === undefined) return ''
  if (isTimeRange(value)) return `from ${value.from} to ${value.to}`
  if (Array.isArray(value)) return value.map((v) => labelForValue(question, v)).join(', ')
  return labelForValue(question, value)
}

function labelForValue(question: AssistantClarificationQuestion, value: string): string {
  const match = question.options?.find((option) => (option.value ?? option.id) === value)
  return match ? `${match.label} (${value})` : value
}

function toLocalInput(iso: string | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const offsetMs = date.getTimezoneOffset() * 60 * 1000
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

function fromLocalInput(local: string): string {
  if (!local) return ''
  const date = new Date(local)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString()
}
